/**
 * WebSocket frame encoding/decoding and request-response correlation.
 *
 * Handles:
 * - JSON frame serialization/deserialization
 * - Request ID generation
 * - Pending request map with promise resolution and timeouts
 */

import type {
  RequestFrame,
  ResponseFrame,
  EventFrame,
  IncomingFrame,
  ProtocolError,
} from '../types/protocol';
import { DEFAULT_REQUEST_TIMEOUT_MS } from '../utils/constants';
import { getErrorMessage } from '../utils/errors';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export class ProtocolResponseError extends Error {
  readonly code: string;
  readonly details?: unknown;

  constructor(error: ProtocolError | unknown) {
    super(getErrorMessage(error, 'Request failed'));
    this.name = 'ProtocolResponseError';

    if (isRecord(error)) {
      this.code = typeof error.code === 'string' ? error.code : 'protocol_error';
      this.details = error.details;
    } else {
      this.code = 'protocol_error';
    }
  }
}

// ── Frame Parsing ──

/** Parse a raw WebSocket message into a typed frame */
export function parseFrame(data: string): IncomingFrame | null {
  try {
    const msg = JSON.parse(data);
    if (msg.type === 'res' || msg.type === 'event') {
      return msg as IncomingFrame;
    }
    console.warn('[frames] Unknown frame type:', msg.type);
    return null;
  } catch (e) {
    console.error('[frames] Failed to parse frame:', e);
    return null;
  }
}

/** Serialize a request frame to JSON */
export function serializeFrame<P>(frame: RequestFrame<P>): string {
  return JSON.stringify(frame);
}

/** Generate a unique request ID */
export function generateRequestId(prefix: string = 'req'): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

/** Build a request frame */
export function buildRequest<P>(
  method: string,
  params: P,
  id?: string,
): RequestFrame<P> {
  return {
    type: 'req',
    id: id ?? generateRequestId(method),
    method,
    params,
  };
}

// ── Frame Type Guards ──

export function isResponseFrame(frame: IncomingFrame): frame is ResponseFrame {
  return frame.type === 'res';
}

export function isEventFrame(frame: IncomingFrame): frame is EventFrame {
  return frame.type === 'event';
}

export function isResponseOk<P>(
  frame: ResponseFrame<P>,
): frame is ResponseFrame<P> & { ok: true; payload: P } {
  return frame.ok === true;
}

// ── Pending Request Map ──

interface PendingRequest<T = unknown> {
  resolve: (payload: T) => void;
  reject: (error: ProtocolError | Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
  method: string;
}

/**
 * Manages pending request-response correlation.
 *
 * Each outgoing request gets a unique ID. The pending map stores
 * a promise resolve/reject pair keyed by ID. When a response arrives,
 * we match it by ID and resolve/reject the promise.
 */
export class PendingRequestMap {
  private pending = new Map<string, PendingRequest>();

  /** Register a pending request. Returns a promise that resolves with the payload. */
  add<T>(id: string, method: string, timeoutMs?: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timeout = timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
      const timeoutId = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`Request timeout: ${method} (${id})`));
      }, timeout);

      this.pending.set(id, {
        resolve: resolve as (payload: unknown) => void,
        reject,
        timeoutId,
        method,
      });
    });
  }

  /** Try to resolve a pending request with a response frame. Returns true if matched. */
  resolve(frame: ResponseFrame): boolean {
    const pending = this.pending.get(frame.id);
    if (!pending) return false;

    clearTimeout(pending.timeoutId);
    this.pending.delete(frame.id);

    if (frame.ok) {
      pending.resolve(frame.payload);
    } else {
      pending.reject(new ProtocolResponseError(frame.error));
    }
    return true;
  }

  /** Reject all pending requests (e.g., on connection close) */
  rejectAll(reason: string): void {
    for (const [id, pending] of this.pending) {
      clearTimeout(pending.timeoutId);
      pending.reject(new Error(reason));
      this.pending.delete(id);
    }
  }

  /** Check if a specific request ID is pending */
  has(id: string): boolean {
    return this.pending.has(id);
  }

  /** Number of pending requests */
  get size(): number {
    return this.pending.size;
  }
}
